import React, { useCallback, useState } from "react";
import classes from "./style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

import Field from "components/Field";
import { getOptions, getBaseUnits, getUnitOptions } from "utils/fields";
import { getModuleSpecs } from "utils/mechanicals";
import { getDecimalPlaces } from "utils/fieldValidation";
import InputRow from "components/Input/Row";
import Input from "components/Input";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Accordion from "components/Accordion";
import Actions from "../Actions";
import Button from "components/Button";
import { getSolarAzimuth } from "utils/weather/api";

import { useStateSelector } from "utils/field";

export default ({
    accessor,
    change,
    contentOpen,
    formName = "",
    isUpgrade = false,
    setAzimSubmitting,
    azimSubmitting,
}) => {
    const [azimuthInputType, setAzimuthInputType] = useState("latLon");

    const systemLabel = useStateSelector(`${accessor}.systemLabel`) || "";
    const manufacturer = useStateSelector(`${accessor}.manufacturer`) || "";
    const model = useStateSelector(`${accessor}.model`) || "";
    const sysNum = useStateSelector(`${accessor}.systemNumber`) || 1;

    const moduleCountValue = useStateSelector(`${accessor}.array.count`);
    const moduleWidthValue = useStateSelector(`${accessor}.array.width`);
    const moduleHeightValue = useStateSelector(`${accessor}.array.height`);
    const arrayAreaValue = useStateSelector(`${accessor}.array.area`);
    const moduleCapacityValue = useStateSelector(`${accessor}.array.moduleCapacity`);
    const arrayCapacityValue = useStateSelector(`${accessor}.array.capacity`);

    const compassDirValue = useStateSelector(`${accessor}.array.compassDir`);
    const latitudeValue = useStateSelector(`${accessor}.array.latitude`);
    const longitudeValue = useStateSelector(`${accessor}.array.longitude`);
    const azimuthValue = useStateSelector(`${accessor}.array.azimuth`);
    const declinationValue = useStateSelector(`${accessor}.array.declination`);
    const postalCodeValue = useStateSelector("model.modelData.general.client.clientPostalCode");
    const clientAddressValue = useStateSelector("model.modelData.general.client.clientStreetAddress");
    const clientCityValue = useStateSelector("model.modelData.general.client.clientCity");

    const arrayAreaUnits = useStateSelector(`${accessor}.array.area_u`);
    const moduleWidthUnits = useStateSelector(`${accessor}.array.width_u`);
    const moduleHeightUnits = useStateSelector(`${accessor}.array.height_u`);
    const moduleCapacityUnits = useStateSelector(`${accessor}.array.moduleCapacity_u`);
    const arrayCapacityUnits = useStateSelector(`${accessor}.array.capacity_u`);

    const { id: moduleTypeId } = useStateSelector(`${accessor}.module.type`) || {};
    const moduleEffValue = useStateSelector(`${accessor}.module.efficiency`);
    const moduleEffUnits = useStateSelector(`${accessor}.module.efficiency_u`);
    const moduleNoctValue = useStateSelector(`${accessor}.module.cellTemperature`);
    const moduleNoctUnits = useStateSelector(`${accessor}.module.cellTemperature_u`);
    const moduleTempCoeffValue = useStateSelector(`${accessor}.module.coefficientOfEfficiency`);
    const moduleTempCoeffUnits = useStateSelector(`${accessor}.module.coefficientOfEfficiency_u`);

    const modelUnits = useStateSelector("model.modelData.uiSettings.primaryUnits") || {};

    const { minor: h2kMinor = 11 } = useStateSelector("model.modelData.h2kVersion") || {};

    const handleGetAzimuth = useCallback(async () => {
        setAzimSubmitting(true);
        let { data: { declination, h2kAzimuth, latitude, longitude } = {} } =
            (await getSolarAzimuth({
                lat: latitudeValue,
                lon: longitudeValue,
                compassDir: compassDirValue,
                searchType: azimuthInputType,
                postalCode:
                    azimuthInputType === "address" ? `${clientAddressValue} ${clientCityValue}` : postalCodeValue,
            })) || {};

        h2kAzimuth = h2kAzimuth ?? 0;
        latitude = latitude ?? 0;
        longitude = longitude ?? 0;
        declination = declination ?? 0;

        if (h2kAzimuth > 180) {
            h2kAzimuth = h2kAzimuth - 360;
        }

        change(`${accessor}.array.azimuth`, parseFloat(h2kAzimuth.toFixed(1)));
        change(`${accessor}.array.latitude`, parseFloat(latitude.toFixed(4)));
        change(`${accessor}.array.longitude`, parseFloat(longitude.toFixed(4)));
        change(`${accessor}.array.declination`, parseFloat(declination.toFixed(4)));

        if (h2kMinor >= 13) {
            change(`${accessor}.orientation.magGeo`, { id: 0 }); //magnetic
            change(`${accessor}.orientation.panelOrientation`, Math.round(compassDirValue));

            const declinationInteger = Math.floor(Math.abs(declination));
            const declinationDecimal = Math.abs(declination) - declinationInteger;

            const minutesValue = Math.round(declinationDecimal * 60);

            change(`${accessor}.orientation.degrees`, parseInt(declinationInteger));
            change(`${accessor}.orientation.minutes`, parseFloat(minutesValue));

            //Negative => Westerly (0), Positive => Easterly (1)
            const declinationId = declination > 0 ? 1 : 0;
            change(`${accessor}.orientation.declination`, { id: declinationId });
        }

        setAzimSubmitting(false);
    }, [
        change,
        azimuthInputType,
        setAzimSubmitting,
        compassDirValue,
        latitudeValue,
        longitudeValue,
        postalCodeValue,
        clientAddressValue,
        clientCityValue,
        h2kMinor,
    ]);

    const createLabel = () => {
        if (systemLabel) {
            return systemLabel;
        } else if (manufacturer && !model) {
            return `${manufacturer}`;
        } else if (manufacturer && model) {
            return `${manufacturer} | ${model}`;
        } else if (!manufacturer && model) {
            return `${model}`;
        } else {
            return `Solar PV Array ${sysNum}`;
        }
    };

    const disableSubmit =
        (azimuthInputType === "latLon" && (!latitudeValue || !longitudeValue)) ||
        (azimuthInputType === "postalCode" && !postalCodeValue) ||
        (azimuthInputType === "address" && (!clientAddressValue || !clientCityValue)) ||
        !compassDirValue;

    return (
        <Accordion
            className={sharedClasses.accordion}
            heading={<span className={sharedClasses.heading}>{createLabel()}</span>}
            large
            hoverActions={<Actions accessor={accessor} change={change} formName={formName} isUpgrade={isUpgrade} />}
            forceOpen={contentOpen}
        >
            <InputRow gridTemplate="1fr 1fr 1fr">
                <Field
                    component={Input}
                    name={`${accessor}.manufacturer`}
                    type="text"
                    label="Manufacturer"
                    placeholder=""
                />
                <Field component={Input} name={`${accessor}.model`} type="text" label="Model" placeholder="" />
            </InputRow>
            <h4>Array Details</h4>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={Input}
                    type="number"
                    name={`${accessor}.array.count`}
                    label="Module Count"
                    placeholder={0}
                    fieldKey={"solarModuleCount"}
                    decimals={getDecimalPlaces("solarModuleCount")}
                    change={change}
                    onChange={(value) => {
                        const newTotalCap = value * moduleCapacityValue;
                        if (!isNaN(newTotalCap)) {
                            change(`${accessor}.array.capacity`, newTotalCap / 1000);
                        }

                        const newArraySize = value * moduleWidthValue * moduleHeightValue;
                        if (!isNaN(newArraySize)) {
                            change(`${accessor}.array.area`, newArraySize);
                        }
                    }}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.array.width`}
                    label="Module Width"
                    placeholder={0.0}
                    fieldKey={"solarModuleWidth"}
                    decimals={getDecimalPlaces("solarModuleWidth")}
                    change={change}
                    units={{
                        base: getBaseUnits("solarModuleWidth", modelUnits),
                        options: getUnitOptions("length"),
                        selected: moduleWidthUnits,
                        unitType: "length",
                        accessor: `${accessor}.array.width_u`,
                    }}
                    onChange={(value) => {
                        const newArraySize = value * moduleCountValue * moduleHeightValue;
                        if (!isNaN(newArraySize)) {
                            change(`${accessor}.array.area`, newArraySize);
                        }
                    }}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.array.height`}
                    label="Module Height"
                    placeholder={0.0}
                    fieldKey={"solarModuleHeight"}
                    decimals={getDecimalPlaces("solarModuleHeight")}
                    change={change}
                    units={{
                        base: getBaseUnits("solarModuleHeight", modelUnits),
                        options: getUnitOptions("length"),
                        selected: moduleHeightUnits,
                        unitType: "length",
                        accessor: `${accessor}.array.height_u`,
                    }}
                    onChange={(value) => {
                        const newArraySize = value * moduleCountValue * moduleWidthValue;
                        if (!isNaN(newArraySize)) {
                            change(`${accessor}.array.area`, newArraySize);
                        }
                    }}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.array.area`}
                    label="Array Area"
                    placeholder={0.0}
                    fieldKey={"solarArrayArea"}
                    decimals={getDecimalPlaces("solarArrayArea")}
                    change={change}
                    setValue={arrayAreaValue}
                    units={{
                        base: getBaseUnits("solarArrayArea", modelUnits),
                        options: getUnitOptions("area"),
                        selected: arrayAreaUnits,
                        unitType: "area",
                        accessor: `${accessor}.array.area_u`,
                    }}
                />
            </InputRow>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.array.moduleCapacity`}
                    label="Single Module Pmax"
                    placeholder={0.0}
                    fieldKey={"solarModuleCapacity"}
                    decimals={getDecimalPlaces("solarModuleCapacity")}
                    change={change}
                    units={{
                        base: getBaseUnits("solarModuleCapacity", modelUnits),
                        options: ["kW", "W"],
                        selected: moduleCapacityUnits,
                        unitType: "power",
                        accessor: `${accessor}.array.moduleCapacity_u`,
                    }}
                    onChange={(value) => {
                        const newTotalCap = value * moduleCountValue;
                        if (!isNaN(newTotalCap)) {
                            change(`${accessor}.array.capacity`, newTotalCap / 1000);
                        }
                    }}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.array.capacity`}
                    label="Array Capacity"
                    placeholder={0.0}
                    fieldKey={"solarArrayCapacity"}
                    decimals={getDecimalPlaces("solarArrayCapacity")}
                    change={change}
                    setValue={arrayCapacityValue}
                    disabled={true}
                    units={{
                        base: getBaseUnits("solarArrayCapacity", modelUnits),
                        options: ["kW", "W"],
                        selected: arrayCapacityUnits,
                        unitType: "power",
                        accessor: `${accessor}.array.capacity_u`,
                    }}
                />
            </InputRow>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Select
                    name="azimuthInputType"
                    label="Azimuth Input Method"
                    placeholder="Choose Input Method"
                    options={[
                        {
                            label: "Lat/Lon",
                            value: "latLon",
                        },
                        {
                            label: "Postal Code",
                            value: "postalCode",
                        },
                        {
                            label: "Address",
                            value: "address",
                        },
                    ]}
                    disabled={false}
                    input={{
                        onChange: (value) => {
                            setAzimuthInputType(value);
                        },
                        value: azimuthInputType,
                    }}
                />
                {azimuthInputType === "latLon" && (
                    <>
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.array.latitude`}
                            label="Latitude"
                            placeholder={0.0}
                            fieldKey={"solarArrayLatitude"}
                            decimals={getDecimalPlaces("solarArrayLatitude")}
                            change={change}
                            units={{
                                base: getBaseUnits("solarArrayLatitude", modelUnits),
                                options: ["°"],
                                selected: "°",
                                unitType: "length",
                                accessor: `${accessor}.array.latitude_u`,
                            }}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.array.longitude`}
                            label="Longitude"
                            placeholder={0.0}
                            fieldKey={"solarArrayLongitude"}
                            decimals={getDecimalPlaces("solarArrayLongitude")}
                            change={change}
                            units={{
                                base: getBaseUnits("solarArrayLongitude", modelUnits),
                                options: ["°"],
                                selected: "°",
                                unitType: "length",
                                accessor: `${accessor}.array.longitude_u`,
                            }}
                            info="Western longitudes are indicated by negative numbers."
                        />
                    </>
                )}
                {azimuthInputType === "postalCode" && (
                    <>
                        <Field
                            component={Input}
                            type="text"
                            name={"model.modelData.general.client.clientPostalCode"}
                            label="Postal Code"
                            placeholder=""
                            fieldKey={"clientPostalCode"}
                            change={change}
                            info="This field is linked to the Client input in the General tab."
                        />
                        <div></div>
                    </>
                )}
                {azimuthInputType === "address" && (
                    <>
                        <Field
                            component={Input}
                            type="text"
                            name={"model.modelData.general.client.clientStreetAddress"}
                            label="Street Address"
                            placeholder=""
                            fieldKey={"clientStreetAddress"}
                            change={change}
                            info="This field is linked to the Client input in the General tab."
                        />
                        <Field
                            component={Input}
                            type="text"
                            name={"model.modelData.general.client.clientCity"}
                            label="City"
                            placeholder=""
                            fieldKey={"clientCity"}
                            change={change}
                            info="This field is linked to the Client input in the General tab."
                        />
                    </>
                )}
                <Button
                    className={classes.getAzimuthButton}
                    onClick={handleGetAzimuth}
                    disabled={azimSubmitting || disableSubmit}
                >
                    Get HOT2000 Azimuth
                </Button>
            </InputRow>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.array.slope`}
                    label="Slope"
                    placeholder={0.0}
                    fieldKey={"solarArraySlope"}
                    decimals={getDecimalPlaces("solarArraySlope")}
                    change={change}
                    units={{
                        base: getBaseUnits("solarArraySlope", modelUnits),
                        options: ["°"],
                        selected: "°",
                        unitType: "length",
                        accessor: `${accessor}.array.slope_u`,
                    }}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.array.compassDir`}
                    label="Array Compass Direction"
                    placeholder={0.0}
                    fieldKey={"solarArrayCompassDir"}
                    decimals={getDecimalPlaces("solarArrayCompassDir")}
                    change={change}
                    units={{
                        base: getBaseUnits("solarArrayCompassDir", modelUnits),
                        options: ["°"],
                        selected: "°",
                        unitType: "length",
                        accessor: `${accessor}.array.compassDir_u`,
                    }}
                    info={"North = 0°,\nEast = 90°,\nSouth = 180°,\nWest = 270°"}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.array.azimuth`}
                    label="HOT2000 Azimuth"
                    placeholder={0.0}
                    fieldKey={"solarArrayAzimuth"}
                    decimals={getDecimalPlaces("solarArrayAzimuth")}
                    change={change}
                    setValue={azimuthValue}
                    units={{
                        base: getBaseUnits("solarArrayAzimuth", modelUnits),
                        options: ["°"],
                        selected: "°",
                        unitType: "length",
                        accessor: `${accessor}.array.azimuth_u`,
                    }}
                    info={"South = 0°,\nEast = 90°,\nWest = -90°,\nNorth = 180°"}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.array.declination`}
                    label="Declination"
                    placeholder={0.0}
                    fieldKey={"solarArrayDeclination"}
                    decimals={getDecimalPlaces("solarArrayDeclination")}
                    change={change}
                    setValue={declinationValue}
                    units={{
                        base: getBaseUnits("solarArrayDeclination", modelUnits),
                        options: ["°"],
                        selected: "°",
                        unitType: "length",
                        accessor: `${accessor}.array.declination_u`,
                    }}
                    info="Negative values indicate westerly, positive indicate easterly."
                />
            </InputRow>
            <h4>Module Specifications</h4>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={Select}
                    name={`${accessor}.module.type`}
                    options={[
                        ...getOptions({ fieldKey: "solarModuleType" }),
                        {
                            label: "User Specified",
                            value: {
                                id: 5,
                            },
                        },
                    ]}
                    label="Module Type"
                    placeholder="Choose Module Type"
                    fieldKey={"solarModuleType"}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.module.efficiency`}
                    label="Efficiency"
                    placeholder={0.0}
                    fieldKey={"solarModuleEff"}
                    decimals={getDecimalPlaces("solarModuleEff")}
                    change={change}
                    units={{
                        base: getBaseUnits("solarModuleEff", modelUnits),
                        options: getUnitOptions("fraction"),
                        selected: moduleEffUnits,
                        unitType: "fraction",
                        accessor: `${accessor}.module.efficiency_u`,
                    }}
                    disabled={moduleTypeId !== 5}
                    setValue={moduleTypeId !== 5 ? getModuleSpecs(moduleTypeId).eff : moduleEffValue}
                    info="At reference temperature of 25˚C / 77˚F"
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.module.cellTemperature`}
                    label="NOCT"
                    placeholder={0.0}
                    fieldKey={"solarModuleNOCT"}
                    decimals={getDecimalPlaces("solarModuleNOCT")}
                    change={change}
                    units={{
                        base: getBaseUnits("solarModuleNOCT", modelUnits),
                        options: getUnitOptions("temperature"),
                        selected: moduleNoctUnits,
                        unitType: "temperature",
                        accessor: `${accessor}.module.cellTemperature_u`,
                    }}
                    disabled={moduleTypeId !== 5}
                    setValue={moduleTypeId !== 5 ? getModuleSpecs(moduleTypeId).noct : moduleNoctValue}
                    info={"Nominal Operating Cell Temperature"}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.module.coefficientOfEfficiency`}
                    label="Temperature Coefficient"
                    placeholder={0.0}
                    fieldKey={"solarModuleTempCoeff"}
                    decimals={getDecimalPlaces("solarModuleTempCoeff")}
                    change={change}
                    units={{
                        base: getBaseUnits("solarModuleTempCoeff", modelUnits),
                        options: getUnitOptions("tempCoeff"),
                        selected: moduleTempCoeffUnits,
                        unitType: "tempCoeff",
                        accessor: `${accessor}.module.coefficientOfEfficiency_u`,
                    }}
                    disabled={moduleTypeId !== 5}
                    setValue={moduleTypeId !== 5 ? getModuleSpecs(moduleTypeId).tempCoeff : moduleTempCoeffValue}
                />
            </InputRow>
            <h4>Losses and Efficiencies</h4>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.effAndLosses.miscLosses`}
                    label="Misc Array Losses"
                    placeholder={0.0}
                    fieldKey={"solarMiscLosses"}
                    decimals={getDecimalPlaces("solarMiscLosses")}
                    change={change}
                    units={{
                        base: getBaseUnits("solarMiscLosses", modelUnits),
                        options: ["%"],
                        selected: ["%"],
                        unitType: "fraction",
                        accessor: `${accessor}.effAndLosses.miscLosses_u`,
                    }}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.effAndLosses.inverterEfficiency`}
                    label="Inverter Efficiency"
                    placeholder={0.0}
                    fieldKey={"solarInverterEff"}
                    decimals={getDecimalPlaces("solarInverterEff")}
                    change={change}
                    units={{
                        base: getBaseUnits("solarInverterEff", modelUnits),
                        options: ["%"],
                        selected: ["%"],
                        unitType: "fraction",
                        accessor: `${accessor}.effAndLosses.inverterEfficiency_u`,
                    }}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.effAndLosses.otherPowerLosses`}
                    label="Other Losses"
                    placeholder={0.0}
                    fieldKey={"solarOtherPowerLosses"}
                    decimals={getDecimalPlaces("solarOtherPowerLosses")}
                    change={change}
                    units={{
                        base: getBaseUnits("solarOtherPowerLosses", modelUnits),
                        options: ["%"],
                        selected: ["%"],
                        unitType: "fraction",
                        accessor: `${accessor}.effAndLosses.otherPowerLosses_u`,
                    }}
                    info={"Other Power Conditioning Losses"}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.effAndLosses.gridAbsorptionRate`}
                    label="Grid Absorption Rate"
                    placeholder={0.0}
                    fieldKey={"solarGridAbsorptionRate"}
                    decimals={getDecimalPlaces("solarGridAbsorptionRate")}
                    change={change}
                    units={{
                        base: getBaseUnits("solarGridAbsorptionRate", modelUnits),
                        options: ["%"],
                        selected: ["%"],
                        unitType: "fraction",
                        accessor: `${accessor}.effAndLosses.gridAbsorptionRate_u`,
                    }}
                />
            </InputRow>
        </Accordion>
    );
};
